/**
 * EditAccountScreen
 * Screen for editing an existing account name and theme
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  SafeAreaView,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
  Alert,
  ScrollView,
} from 'react-native';
import { EditAccountScreenProps } from '../navigation/types';
import { useGoals } from '../hooks/useGoals';
import { THEME_COLORS } from '../models/GoalAccount';

export const EditAccountScreen: React.FC<EditAccountScreenProps> = ({ navigation, route }) => {
  const { accounts, updateAccountSafe, deleteAccountSafe } = useGoals();
  const { accountId } = route.params;

  const account = accounts.find(a => a.id === accountId);

  const [accountName, setAccountName] = useState(account?.name || '');
  const [selectedColor, setSelectedColor] = useState(account?.themeColor || THEME_COLORS[0].color);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (account) {
      setAccountName(account.name);
      setSelectedColor(account.themeColor || THEME_COLORS[0].color);
    }
  }, [account]);

  if (!account) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>Account not found</Text>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Text style={styles.backButtonText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  const handleSave = async () => {
    // Validate
    const trimmedName = accountName.trim();
    if (trimmedName.length === 0) {
      Alert.alert('Error', 'Please enter an account name');
      return;
    }

    try {
      setIsSaving(true);

      // Update account with new name and theme color
      await updateAccountSafe(accountId, {
        name: trimmedName,
        themeColor: selectedColor,
      });

      // Navigate back
      navigation.goBack();
    } catch (error) {
      Alert.alert('Error', error instanceof Error ? error.message : 'Failed to update account');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (Platform.OS === 'web') {
      // Use window.confirm on web since Alert.alert doesn't work properly
      const confirmed = window.confirm(
        `Are you sure you want to delete "${account?.name}"? This will permanently delete all goals and completion history for this journey. This action cannot be undone.`
      );
      if (!confirmed) return;

      try {
        setIsSaving(true);
        await deleteAccountSafe(accountId);
        navigation.navigate('AccountList');
      } catch (error) {
        window.alert(error instanceof Error ? error.message : 'Failed to delete journey');
      } finally {
        setIsSaving(false);
      }
    } else {
      Alert.alert(
        'Delete Journey',
        `Are you sure you want to delete "${account?.name}"? This will permanently delete all goals and completion history for this journey. This action cannot be undone.`,
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                setIsSaving(true);
                await deleteAccountSafe(accountId);
                navigation.navigate('AccountList');
                Alert.alert('Journey Deleted', 'The journey has been permanently deleted.');
              } catch (error) {
                Alert.alert(
                  'Cannot Delete Journey',
                  error instanceof Error ? error.message : 'Failed to delete journey'
                );
              } finally {
                setIsSaving(false);
              }
            },
          },
        ]
      );
    }
  };

  // Get lighter background color based on theme
  const getBackgroundColor = (color: string): string => {
    // Convert hex to RGB and create a very light tint
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Mix with white (95% white, 5% theme color)
    const mixedR = Math.round(r * 0.05 + 255 * 0.95);
    const mixedG = Math.round(g * 0.05 + 255 * 0.95);
    const mixedB = Math.round(b * 0.05 + 255 * 0.95);

    return `rgb(${mixedR}, ${mixedG}, ${mixedB})`;
  };

  const backgroundColor = getBackgroundColor(selectedColor);

  return (
    <SafeAreaView style={[styles.container, { backgroundColor }]}>
      <KeyboardAvoidingView
        style={styles.keyboardView}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
          <View style={styles.content}>
            {/* Header */}
            <View style={styles.header}>
              <Text style={[styles.title, { color: selectedColor }]}>Edit Journey</Text>
              <Text style={styles.subtitle}>
                Update your journey name and theme color
              </Text>
            </View>

            {/* Input */}
            <View style={styles.inputContainer}>
              <Text style={styles.label}>Journey Name</Text>
              <TextInput
                style={styles.input}
                placeholder="e.g., Mind, Body, Soul"
                placeholderTextColor="#999"
                value={accountName}
                onChangeText={setAccountName}
                autoCapitalize="words"
                returnKeyType="done"
                onSubmitEditing={handleSave}
                editable={!isSaving}
              />
            </View>

            {/* Color Picker */}
            <View style={styles.colorPickerContainer}>
              <Text style={styles.label}>Theme Color</Text>
              <View style={styles.colorGrid}>
                {THEME_COLORS.map((theme) => (
                  <TouchableOpacity
                    key={theme.color}
                    style={[
                      styles.colorOption,
                      { backgroundColor: theme.color },
                      selectedColor === theme.color && styles.colorOptionSelected,
                    ]}
                    onPress={() => setSelectedColor(theme.color)}
                    disabled={isSaving}
                    accessibilityLabel={`${theme.name} theme`}
                    accessibilityHint={selectedColor === theme.color ? 'Currently selected' : 'Tap to select'}
                  >
                    {selectedColor === theme.color && (
                      <Text style={styles.checkmark}>✓</Text>
                    )}
                  </TouchableOpacity>
                ))}
              </View>
              <Text style={styles.colorHint}>
                Theme color affects accents throughout the app
              </Text>
            </View>

            {/* Save Button */}
            <TouchableOpacity
              style={[styles.saveButton, { backgroundColor: selectedColor }, isSaving && styles.saveButtonDisabled]}
              onPress={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={styles.saveButtonText}>Save Changes</Text>
              )}
            </TouchableOpacity>

            {/* Cancel Button */}
            <TouchableOpacity
              style={styles.cancelButton}
              onPress={() => navigation.goBack()}
              disabled={isSaving}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>

            {/* Delete Button */}
            <View style={styles.dangerZone}>
              <Text style={styles.dangerZoneTitle}>Danger Zone</Text>
              <TouchableOpacity
                style={styles.deleteButton}
                onPress={handleDelete}
                disabled={isSaving}
              >
                <Text style={styles.deleteButtonText}>Delete Journey</Text>
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  keyboardView: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  content: {
    flex: 1,
    padding: 24,
  },
  header: {
    marginBottom: 32,
    marginTop: 16,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    marginBottom: 12,
  },
  subtitle: {
    fontSize: 16,
    color: '#666',
    lineHeight: 24,
  },
  inputContainer: {
    marginBottom: 24,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  input: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    fontSize: 16,
    color: '#333',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  colorPickerContainer: {
    marginBottom: 24,
  },
  colorGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 8,
  },
  colorOption: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 3,
    borderColor: 'transparent',
  },
  colorOptionSelected: {
    borderColor: '#333',
    borderWidth: 3,
  },
  checkmark: {
    fontSize: 24,
    color: '#fff',
    fontWeight: '700',
  },
  colorHint: {
    fontSize: 13,
    color: '#999',
    marginTop: 4,
  },
  saveButton: {
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 12,
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  cancelButton: {
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    marginBottom: 32,
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#666',
  },
  dangerZone: {
    marginTop: 'auto',
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  dangerZoneTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#999',
    marginBottom: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  deleteButton: {
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: 'center',
    backgroundColor: '#FFE5E5',
    borderWidth: 1,
    borderColor: '#FF3B30',
  },
  deleteButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FF3B30',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  errorText: {
    fontSize: 18,
    color: '#666',
    marginBottom: 24,
  },
  backButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
  },
  backButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
});
